import {
  ANIMATION_TYPES,
  CANVAS_SIZE_PRESETS,
  EASING_TYPES,
  LOOP_MODES,
  MAX_ANIMATION_SEGMENT_COUNT,
  MAX_ANIMATION_SEGMENT_WEIGHT,
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  MAX_LAYOUT_OFFSET_COUNT,
  MIN_CANVAS_DIMENSION,
  MODULE_SHAPES,
  NAMED_CANVAS_PRESET_DIMENSIONS,
  PLAYBACK_DIRECTIONS,
  RENDERER_PREFERENCES,
  TEXT_ALIGNS,
  TEXT_BASELINES,
  type CanvasSizePreset,
  type AnimationSegment,
  type CustomEasingCurve,
  type CustomSvgData,
  type Offset2D,
  type ProjectDocumentV2,
  type RGBColor,
} from '../project/ProjectDocument.js'
import { validateCustomSvgData } from '../project/validateCustomSvg.js'

export interface EditableFieldMap {
  seed: ProjectDocumentV2['seed']
  'text.content': ProjectDocumentV2['text']['content']
  'text.activeFont': ProjectDocumentV2['text']['activeFont']
  'text.fontSize': ProjectDocumentV2['text']['fontSize']
  'text.lineHeight': ProjectDocumentV2['text']['lineHeight']
  'text.wordSpacing': ProjectDocumentV2['text']['wordSpacing']
  'text.textAlign': ProjectDocumentV2['text']['textAlign']
  'text.baseline': ProjectDocumentV2['text']['baseline']
  'particles.shape': ProjectDocumentV2['particles']['shape']
  'particles.density': ProjectDocumentV2['particles']['density']
  'particles.scale': ProjectDocumentV2['particles']['scale']
  'particles.customSvg': ProjectDocumentV2['particles']['customSvg']
  'appearance.color': ProjectDocumentV2['appearance']['color']
  'appearance.chaosColor': ProjectDocumentV2['appearance']['chaosColor']
  'appearance.fadeInChaos': ProjectDocumentV2['appearance']['fadeInChaos']
  'appearance.gridMode': ProjectDocumentV2['appearance']['gridMode']
  'appearance.colorTransitionStart': ProjectDocumentV2['appearance']['colorTransitionStart']
  'appearance.colorTransitionEnd': ProjectDocumentV2['appearance']['colorTransitionEnd']
  'appearance.showBackground': ProjectDocumentV2['appearance']['showBackground']
  'appearance.backgroundColor': ProjectDocumentV2['appearance']['backgroundColor']
  'appearance.showBorder': ProjectDocumentV2['appearance']['showBorder']
  'appearance.borderIntensity': ProjectDocumentV2['appearance']['borderIntensity']
  'appearance.borderColor': ProjectDocumentV2['appearance']['borderColor']
  'appearance.borderRadius': ProjectDocumentV2['appearance']['borderRadius']
  'appearance.borderOnTop': ProjectDocumentV2['appearance']['borderOnTop']
  'appearance.borderScale': ProjectDocumentV2['appearance']['borderScale']
  'appearance.showGlow': ProjectDocumentV2['appearance']['showGlow']
  'appearance.glowIntensity': ProjectDocumentV2['appearance']['glowIntensity']
  'appearance.glowColor': ProjectDocumentV2['appearance']['glowColor']
  'appearance.glowSoftness': ProjectDocumentV2['appearance']['glowSoftness']
  'animation.type': ProjectDocumentV2['animation']['type']
  'animation.segments': ProjectDocumentV2['animation']['segments']
  'animation.speed': ProjectDocumentV2['animation']['speed']
  'animation.easingType': ProjectDocumentV2['animation']['easingType']
  'animation.easingAmount': ProjectDocumentV2['animation']['easingAmount']
  'animation.customEasingCurve': ProjectDocumentV2['animation']['customEasingCurve']
  'animation.playbackDirection': ProjectDocumentV2['animation']['playbackDirection']
  'animation.holdAtOrder': ProjectDocumentV2['animation']['holdAtOrder']
  'animation.holdAtChaos': ProjectDocumentV2['animation']['holdAtChaos']
  'animation.loopMode': ProjectDocumentV2['animation']['loopMode']
  'animation.wiggleEnabled': ProjectDocumentV2['animation']['wiggleEnabled']
  'animation.wiggleSpeed': ProjectDocumentV2['animation']['wiggleSpeed']
  'animation.wiggleForce': ProjectDocumentV2['animation']['wiggleForce']
  'animation.wiggleDampening': ProjectDocumentV2['animation']['wiggleDampening']
  'animation.rotationEnabled': ProjectDocumentV2['animation']['rotationEnabled']
  'animation.rotationSpeed': ProjectDocumentV2['animation']['rotationSpeed']
  'animation.motionBlur': ProjectDocumentV2['animation']['motionBlur']
  'layout.letterMode': ProjectDocumentV2['layout']['letterMode']
  'layout.dotMode': ProjectDocumentV2['layout']['dotMode']
  'canvas.preferredRenderer': ProjectDocumentV2['canvas']['preferredRenderer']
}

export type EditableFieldPath = Extract<keyof EditableFieldMap, string>

export const EDITABLE_FIELD_PATHS = [
  'seed',
  'text.content', 'text.activeFont', 'text.fontSize', 'text.lineHeight', 'text.wordSpacing',
  'text.textAlign', 'text.baseline',
  'particles.shape', 'particles.density', 'particles.scale', 'particles.customSvg',
  'appearance.color', 'appearance.chaosColor', 'appearance.fadeInChaos', 'appearance.gridMode',
  'appearance.colorTransitionStart', 'appearance.colorTransitionEnd', 'appearance.showBackground',
  'appearance.backgroundColor', 'appearance.showBorder', 'appearance.borderIntensity',
  'appearance.borderColor', 'appearance.borderRadius', 'appearance.borderOnTop',
  'appearance.borderScale', 'appearance.showGlow', 'appearance.glowIntensity',
  'appearance.glowColor', 'appearance.glowSoftness',
  'animation.type', 'animation.segments', 'animation.speed', 'animation.easingType', 'animation.easingAmount',
  'animation.customEasingCurve', 'animation.playbackDirection', 'animation.holdAtOrder',
  'animation.holdAtChaos', 'animation.loopMode', 'animation.wiggleEnabled',
  'animation.wiggleSpeed', 'animation.wiggleForce', 'animation.wiggleDampening',
  'animation.rotationEnabled', 'animation.rotationSpeed',
  'animation.motionBlur',
  'layout.letterMode', 'layout.dotMode', 'canvas.preferredRenderer',
] as const satisfies readonly EditableFieldPath[]

type PersistedFieldPath = 'version' | 'seed' | {
  [Section in Exclude<keyof ProjectDocumentV2, 'version' | 'seed'>]:
    `${Extract<Section, string>}.${Extract<keyof ProjectDocumentV2[Section], string>}`
}[Exclude<keyof ProjectDocumentV2, 'version' | 'seed'>]
type ExplicitAtomicPath =
  | 'version'
  | 'canvas.width' | 'canvas.height' | 'canvas.preset'
  | 'layout.wordOffsets' | 'layout.letterOffsets' | 'layout.dotOffsets'
type ClassifiedFieldPath = EditableFieldPath | ExplicitAtomicPath
type ExactCoverage =
  Exclude<PersistedFieldPath, ClassifiedFieldPath> extends never
    ? Exclude<ClassifiedFieldPath, PersistedFieldPath> extends never
      ? Exclude<EditableFieldPath, (typeof EDITABLE_FIELD_PATHS)[number]> extends never
        ? true
        : never
      : never
    : never
export const EDITOR_SCHEMA_CLASSIFICATION_COMPLETE: ExactCoverage = true

export type SetFieldCommand = {
  [Path in EditableFieldPath]: {
    readonly type: 'document/set-field'
    readonly path: Path
    readonly value: EditableFieldMap[Path]
  }
}[EditableFieldPath]

export type OffsetTarget = 'word' | 'letter' | 'dot'

export type DocumentCommand =
  | SetFieldCommand
  | { readonly type: 'document/set-canvas'; readonly width: number; readonly height: number; readonly preset: CanvasSizePreset }
  | { readonly type: 'document/set-offset'; readonly target: OffsetTarget; readonly index: number; readonly value: Offset2D }
  | { readonly type: 'document/set-offsets'; readonly target: OffsetTarget; readonly values: readonly Offset2D[] }
  | { readonly type: 'document/patch-offsets'; readonly target: OffsetTarget; readonly updates: readonly { readonly index: number; readonly value: Offset2D }[] }
  | { readonly type: 'document/reset-offsets'; readonly target: OffsetTarget }

export interface EditorViewport {
  readonly x: number
  readonly y: number
  readonly zoom: number
}

export type PlaybackPhase = 'moving' | 'holding-order' | 'holding-chaos' | 'complete'

export interface PlaybackFrame {
  readonly orderAmount: number
  readonly isPlaying: boolean
  readonly direction: 1 | -1
  readonly phase: PlaybackPhase
}

export interface DetectedDotInput {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly relativeX: number
  readonly relativeY: number
  readonly wordIndex: number
  readonly letterIndex: number
}

export type TransientCommand =
  | { readonly type: 'transient/set-viewport'; readonly value: EditorViewport }
  | { readonly type: 'transient/reset-viewport' }
  | { readonly type: 'transient/set-playback-frame'; readonly value: PlaybackFrame }
  | { readonly type: 'transient/set-playing'; readonly value: boolean }
  | { readonly type: 'transient/set-rendering-enabled'; readonly value: boolean }
  | { readonly type: 'transient/set-detected-dots'; readonly value: readonly DetectedDotInput[] }
  | { readonly type: 'transient/set-selection'; readonly target: OffsetTarget; readonly index: number | null }
  | { readonly type: 'transient/clear-selection' }

export type EditorCommand = DocumentCommand | TransientCommand

export const MAX_OFFSET_PATCH_SCRATCH_BYTES = MAX_LAYOUT_OFFSET_COUNT * (
  Int32Array.BYTES_PER_ELEMENT
  + Uint32Array.BYTES_PER_ELEMENT
  + Float64Array.BYTES_PER_ELEMENT * 2
)

export class EditorCommandError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EditorCommandError'
  }
}

function fail(message: string): never {
  throw new EditorCommandError(message)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finiteNumber(value: unknown, minimum: number, maximum: number, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
    fail(`${label} must be finite and within ${minimum}…${maximum}`)
  }
  return value
}

function integer(value: unknown, minimum: number, maximum: number, label: string): number {
  const number = finiteNumber(value, minimum, maximum, label)
  if (!Number.isSafeInteger(number)) fail(`${label} must be a safe integer`)
  return number
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== 'string') fail(`${label} must be a string`)
  return value
}

function booleanValue(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') fail(`${label} must be boolean`)
  return value
}

function enumValue<const Value extends string>(value: unknown, allowed: readonly Value[], label: string): Value {
  if (typeof value !== 'string' || !allowed.includes(value as Value)) fail(`${label} is not a supported enum value`)
  return value as Value
}

function colorValue(value: unknown, label: string): Readonly<RGBColor> {
  if (!isRecord(value)) fail(`${label} must be an RGB object`)
  return Object.freeze({
    r: finiteNumber(value.r, 0, 255, `${label}.r`),
    g: finiteNumber(value.g, 0, 255, `${label}.g`),
    b: finiteNumber(value.b, 0, 255, `${label}.b`),
  })
}

function pointValue(value: unknown, minimum: number, maximum: number, label: string): Readonly<Offset2D> {
  if (!isRecord(value)) fail(`${label} must be an x/y object`)
  return Object.freeze({
    x: finiteNumber(value.x, minimum, maximum, `${label}.x`),
    y: finiteNumber(value.y, minimum, maximum, `${label}.y`),
  })
}

function curveValue(value: unknown): Readonly<CustomEasingCurve> {
  if (!isRecord(value)) fail('Custom easing curve must be an object')
  const p1 = pointValue(value.p1, -2, 3, 'Custom easing p1')
  const p2 = pointValue(value.p2, -2, 3, 'Custom easing p2')
  if (p1.x < 0 || p1.x > 1 || p2.x < 0 || p2.x > 1) {
    fail('Custom easing x coordinates must be within 0…1')
  }
  return Object.freeze({ p1, p2 })
}

function svgValue(value: unknown): Readonly<CustomSvgData> | null {
  if (value === null) return null
  const validated = validateCustomSvgData(value)
  if (validated === null) fail('Custom SVG input is invalid')
  return Object.freeze({
    dataUrl: validated.dataUrl,
    name: validated.name,
    viewBox: Object.freeze({ ...validated.viewBox }),
  })
}

function animationSegmentsValue(value: unknown): ReadonlyArray<Readonly<AnimationSegment>> | undefined {
  if (value === undefined || (Array.isArray(value) && value.length === 0)) return undefined
  if (!Array.isArray(value)) fail('Animation segments must be an array')
  if (value.length > MAX_ANIMATION_SEGMENT_COUNT) {
    fail(`Animation segments exceeds the ${MAX_ANIMATION_SEGMENT_COUNT} entry cap`)
  }
  const segments = new Array<Readonly<AnimationSegment>>(value.length)
  for (let index = 0; index < value.length; index += 1) {
    const entry: unknown = value[index]
    if (!isRecord(entry)) fail(`Animation segments[${index}] must be an object`)
    segments[index] = Object.freeze({
      type: enumValue(entry.type, ANIMATION_TYPES, `Animation segments[${index}].type`),
      weight: finiteNumber(
        entry.weight,
        Number.MIN_VALUE,
        MAX_ANIMATION_SEGMENT_WEIGHT,
        `Animation segments[${index}].weight`,
      ),
    })
  }
  return Object.freeze(segments)
}

function validateField(path: EditableFieldPath, value: unknown): unknown {
  switch (path) {
    case 'seed': return integer(value, 0, 0xffff_ffff, 'Project seed')
    case 'text.content': return stringValue(value, 'Text content')
    case 'text.activeFont': return stringValue(value, 'Active font')
    case 'text.fontSize': return finiteNumber(value, 24, 300, 'Font size')
    case 'text.lineHeight': return finiteNumber(value, 1, 4, 'Line height')
    case 'text.wordSpacing': return finiteNumber(value, 0, 2, 'Word spacing')
    case 'text.textAlign': return enumValue(value, TEXT_ALIGNS, 'Text alignment')
    case 'text.baseline': return enumValue(value, TEXT_BASELINES, 'Text baseline')
    case 'particles.shape': return enumValue(value, MODULE_SHAPES, 'Particle shape')
    case 'particles.density': return finiteNumber(value, 1, 25, 'Particle density')
    case 'particles.scale': return finiteNumber(value, 0.2, 3, 'Particle scale')
    case 'particles.customSvg': return svgValue(value)
    case 'appearance.color':
    case 'appearance.chaosColor':
    case 'appearance.backgroundColor':
    case 'appearance.borderColor':
    case 'appearance.glowColor': return colorValue(value, path)
    case 'appearance.fadeInChaos':
    case 'appearance.gridMode':
    case 'appearance.showBackground':
    case 'appearance.showBorder':
    case 'appearance.borderOnTop':
    case 'appearance.showGlow':
    case 'animation.wiggleEnabled':
    case 'animation.rotationEnabled':
    case 'layout.letterMode':
    case 'layout.dotMode': return booleanValue(value, path)
    case 'appearance.colorTransitionStart':
    case 'appearance.colorTransitionEnd':
    case 'appearance.borderIntensity':
    case 'appearance.glowIntensity':
    case 'animation.easingAmount':
    case 'animation.motionBlur': return finiteNumber(value, 0, 1, path)
    case 'appearance.borderRadius': return finiteNumber(value, 0, 0.5, path)
    case 'appearance.borderScale': return finiteNumber(value, 0.5, 3, path)
    case 'appearance.glowSoftness': return finiteNumber(value, 0, 60, path)
    case 'animation.type': return enumValue(value, ANIMATION_TYPES, 'Animation type')
    case 'animation.segments': return animationSegmentsValue(value)
    case 'animation.speed': return finiteNumber(value, 0.1, 100, 'Animation speed')
    case 'animation.easingType': return enumValue(value, EASING_TYPES, 'Easing type')
    case 'animation.customEasingCurve': return curveValue(value)
    case 'animation.playbackDirection': return enumValue(value, PLAYBACK_DIRECTIONS, 'Playback direction')
    case 'animation.holdAtOrder':
    case 'animation.holdAtChaos': return finiteNumber(value, 0, 30, path)
    case 'animation.loopMode': return enumValue(value, LOOP_MODES, 'Loop mode')
    case 'animation.wiggleSpeed':
    case 'animation.wiggleForce': return finiteNumber(value, 0, 20, path)
    case 'animation.rotationSpeed': return finiteNumber(value, 0, 10, path)
    case 'animation.wiggleDampening': return finiteNumber(value, 0.1, 1, path)
    case 'canvas.preferredRenderer': return enumValue(value, RENDERER_PREFERENCES, 'Renderer preference')
    default: {
      const exhaustive: never = path
      return exhaustive
    }
  }
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false
    for (let index = 0; index < left.length; index += 1) {
      if (!valuesEqual(left[index], right[index])) return false
    }
    return true
  }
  if (isRecord(left) && isRecord(right)) {
    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)
    if (leftKeys.length !== rightKeys.length) return false
    for (const key of leftKeys) {
      if (!Object.prototype.hasOwnProperty.call(right, key) || !valuesEqual(left[key], right[key])) return false
    }
    return true
  }
  return false
}

export function documentsSemanticallyEqual(left: ProjectDocumentV2, right: ProjectDocumentV2): boolean {
  return valuesEqual(left, right)
}

function isEditableFieldPath(value: unknown): value is EditableFieldPath {
  return typeof value === 'string' && (EDITABLE_FIELD_PATHS as readonly string[]).includes(value)
}

function setField(document: ProjectDocumentV2, pathValue: unknown, value: unknown): ProjectDocumentV2 {
  if (!isEditableFieldPath(pathValue)) fail('Document field path is invalid')
  const path = pathValue
  const validated = validateField(path, value)
  if (path === 'seed') {
    if (Object.is(document.seed, validated)) return document
    return Object.freeze({ ...document, seed: validated as number })
  }
  const separator = path.indexOf('.')
  const section = path.slice(0, separator) as Exclude<keyof ProjectDocumentV2, 'version' | 'seed'>
  const key = path.slice(separator + 1)
  const currentSection = document[section] as unknown as Record<string, unknown>
  if (valuesEqual(currentSection[key], validated)) return document
  if (path === 'animation.segments' && validated === undefined) {
    if (!Object.prototype.hasOwnProperty.call(currentSection, key)) return document
    const nextSection = { ...currentSection }
    delete nextSection[key]
    return Object.freeze({ ...document, [section]: Object.freeze(nextSection) }) as ProjectDocumentV2
  }
  const nextSection = Object.freeze({ ...currentSection, [key]: validated })
  return Object.freeze({ ...document, [section]: nextSection }) as ProjectDocumentV2
}

function offsetKey(target: unknown): 'wordOffsets' | 'letterOffsets' | 'dotOffsets' {
  if (target === 'word') return 'wordOffsets'
  if (target === 'letter') return 'letterOffsets'
  if (target === 'dot') return 'dotOffsets'
  return fail('Offset target is invalid')
}

function withOffsets(
  document: ProjectDocumentV2,
  key: 'wordOffsets' | 'letterOffsets' | 'dotOffsets',
  next: readonly Readonly<Offset2D>[],
): ProjectDocumentV2 {
  const layout = Object.freeze({ ...document.layout, [key]: next })
  return Object.freeze({ ...document, layout }) as ProjectDocumentV2
}

function replaceOffsets(
  document: ProjectDocumentV2,
  key: 'wordOffsets' | 'letterOffsets' | 'dotOffsets',
  next: readonly Readonly<Offset2D>[],
): ProjectDocumentV2 {
  if (valuesEqual(document.layout[key], next)) return document
  return withOffsets(document, key, next)
}

function setCanvas(
  document: ProjectDocumentV2,
  widthValue: unknown,
  heightValue: unknown,
  presetValue: unknown,
): ProjectDocumentV2 {
  const width = integer(widthValue, MIN_CANVAS_DIMENSION, MAX_CANVAS_DIMENSION, 'Canvas width')
  const height = integer(heightValue, MIN_CANVAS_DIMENSION, MAX_CANVAS_DIMENSION, 'Canvas height')
  if (width * height > MAX_CANVAS_PIXEL_AREA) fail('Canvas pixel area exceeds the supported budget')
  const preset = enumValue(presetValue, CANVAS_SIZE_PRESETS, 'Canvas preset')
  if (preset !== 'custom') {
    const dimensions = NAMED_CANVAS_PRESET_DIMENSIONS[preset]
    if (dimensions.width !== width || dimensions.height !== height) {
      fail('Named canvas preset dimensions must match exactly')
    }
  }
  if (document.canvas.width === width && document.canvas.height === height && document.canvas.preset === preset) {
    return document
  }
  const canvas = Object.freeze({ ...document.canvas, width, height, preset })
  return Object.freeze({ ...document, canvas })
}

export function readEditorCommandType(command: unknown): string {
  if (!isRecord(command)) fail('Editor command must be an object')
  const type: unknown = command.type
  if (typeof type !== 'string') fail('Editor command type must be a string')
  return type
}

export function applyDocumentCommand(
  document: ProjectDocumentV2,
  command: unknown,
  snapshottedType?: string,
): ProjectDocumentV2 {
  if (!isRecord(command)) fail('Document command must be an object')
  const type = snapshottedType ?? readEditorCommandType(command)
  switch (type) {
    case 'document/set-field': {
      const path: unknown = command.path
      const value: unknown = command.value
      return setField(document, path, value)
    }
    case 'document/set-canvas': {
      const width: unknown = command.width
      const height: unknown = command.height
      const preset: unknown = command.preset
      return setCanvas(document, width, height, preset)
    }
    case 'document/set-offset': {
      const target: unknown = command.target
      const indexValue: unknown = command.index
      const rawValue: unknown = command.value
      const key = offsetKey(target)
      const index = integer(indexValue, 0, MAX_LAYOUT_OFFSET_COUNT - 1, 'Offset index')
      if (!isRecord(rawValue)) fail('Offset value must be an x/y object')
      const x = finiteNumber(rawValue.x, -100_000, 100_000, 'Offset value.x')
      const y = finiteNumber(rawValue.y, -100_000, 100_000, 'Offset value.y')
      const current = document.layout[key]
      const currentValue = current[index]
      if (currentValue !== undefined && Object.is(currentValue.x, x) && Object.is(currentValue.y, y)) {
        return document
      }
      const next = current.slice() as Readonly<Offset2D>[]
      while (next.length <= index) next.push(Object.freeze({ x: 0, y: 0 }))
      next[index] = Object.freeze({ x, y })
      return withOffsets(document, key, Object.freeze(next))
    }
    case 'document/set-offsets': {
      const target: unknown = command.target
      const rawValues: unknown = command.values
      const key = offsetKey(target)
      if (!Array.isArray(rawValues)) fail('Offset values must be an array')
      const length = rawValues.length
      if (length > MAX_LAYOUT_OFFSET_COUNT) fail('Offsets exceeds the offset count cap')
      const current = document.layout[key]
      let next: Readonly<Offset2D>[] | null = current.length === length
        ? null
        : new Array<Readonly<Offset2D>>(length)
      for (let index = 0; index < length; index += 1) {
        const rawValue: unknown = rawValues[index]
        if (!isRecord(rawValue)) fail(`Offsets[${index}] must be an x/y object`)
        const x = finiteNumber(rawValue.x, -100_000, 100_000, 'Offset x')
        const y = finiteNumber(rawValue.y, -100_000, 100_000, 'Offset y')
        const currentValue = current[index]
        const unchanged = currentValue !== undefined
          && Object.is(currentValue.x, x)
          && Object.is(currentValue.y, y)
        if (next === null && !unchanged) {
          next = new Array<Readonly<Offset2D>>(length)
          for (let sharedIndex = 0; sharedIndex < index; sharedIndex += 1) {
            const shared = current[sharedIndex]
            if (shared !== undefined) next[sharedIndex] = shared
          }
        }
        if (next !== null) next[index] = unchanged ? currentValue : Object.freeze({ x, y })
      }
      if (next === null) return document
      return withOffsets(document, key, Object.freeze(next))
    }
    case 'document/patch-offsets': {
      const target: unknown = command.target
      const rawUpdates: unknown = command.updates
      const key = offsetKey(target)
      if (!Array.isArray(rawUpdates)) fail('Offset patch must be a bounded array')
      const updateCount = rawUpdates.length
      if (updateCount > MAX_LAYOUT_OFFSET_COUNT) {
        fail('Offset patch must be a bounded array')
      }
      if (updateCount === 0) return document
      const positionByIndex = new Int32Array(MAX_LAYOUT_OFFSET_COUNT)
      const indices = new Uint32Array(updateCount)
      const xValues = new Float64Array(updateCount)
      const yValues = new Float64Array(updateCount)
      let uniqueCount = 0
      let maximumIndex = -1
      for (let updateIndex = 0; updateIndex < updateCount; updateIndex += 1) {
        const update: unknown = rawUpdates[updateIndex]
        if (!isRecord(update)) fail('Offset patch entry must be an object')
        const rawIndex: unknown = update.index
        const rawValue: unknown = update.value
        const index = integer(rawIndex, 0, MAX_LAYOUT_OFFSET_COUNT - 1, 'Offset patch index')
        if (!isRecord(rawValue)) fail('Offset patch value must be an x/y object')
        const x = finiteNumber(rawValue.x, -100_000, 100_000, 'Offset patch value.x')
        const y = finiteNumber(rawValue.y, -100_000, 100_000, 'Offset patch value.y')
        const encodedPosition = positionByIndex[index] ?? 0
        let position = encodedPosition - 1
        if (encodedPosition === 0) {
          position = uniqueCount
          uniqueCount += 1
          positionByIndex[index] = position + 1
          indices[position] = index
        }
        xValues[position] = x
        yValues[position] = y
        maximumIndex = Math.max(maximumIndex, index)
      }
      const current = document.layout[key]
      let changedCount = 0
      for (let position = 0; position < uniqueCount; position += 1) {
        const index = indices[position]
        if (index === undefined) continue
        const currentValue = current[index]
        const x = xValues[position] ?? 0
        const y = yValues[position] ?? 0
        if (currentValue === undefined || !Object.is(currentValue.x, x) || !Object.is(currentValue.y, y)) {
          indices[changedCount] = index
          xValues[changedCount] = x
          yValues[changedCount] = y
          changedCount += 1
        }
      }
      if (changedCount === 0) return document
      const next = current.slice() as Readonly<Offset2D>[]
      while (next.length <= maximumIndex) next.push(Object.freeze({ x: 0, y: 0 }))
      for (let position = 0; position < changedCount; position += 1) {
        const index = indices[position]
        if (index === undefined) continue
        const x = xValues[position] ?? 0
        const y = yValues[position] ?? 0
        next[index] = Object.freeze({ x, y })
      }
      return withOffsets(document, key, Object.freeze(next))
    }
    case 'document/reset-offsets': {
      const target: unknown = command.target
      return replaceOffsets(document, offsetKey(target), Object.freeze([]))
    }
    default: return fail('Document command type is invalid')
  }
}

export function isDocumentCommand(command: unknown): command is DocumentCommand {
  if (!isRecord(command)) return false
  const type: unknown = command.type
  return typeof type === 'string' && type.startsWith('document/')
}
